import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  TOKEN_STORAGE_KEY,
  clearAuthSession,
  getMe,
  getStoredUser,
  login as loginRequest,
  setAuthSession,
  signup as signupRequest
} from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(localStorage.getItem(TOKEN_STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const me = await getMe();
        setUser(me);
        setAuthSession(token, me);
      } catch {
        clearAuthSession();
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [token]);

  const signup = async (payload) => {
    const data = await signupRequest(payload);
    setAuthSession(data.access_token, data.user);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const login = async (payload) => {
    const data = await loginRequest(payload);
    setAuthSession(data.access_token, data.user);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    clearAuthSession();
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      token,
      role: user?.role || null,
      account_type: user?.account_type || null,
      loading,
      isAuthenticated: Boolean(user && token),
      login,
      signup,
      logout
    }),
    [loading, token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
